import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Button
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';

const featuresContent = [
  {
    icon: <TimelineRoundedIcon color="primary" sx={{ fontSize: 28 }} />,
    title: 'Full-stack scheduling',
    body: 'Build scene calendars, generate call sheets, and sync with smart availability so every department stays on track.'
  },
  {
    icon: <GroupsRoundedIcon color="primary" sx={{ fontSize: 28 }} />,
    title: 'Department collaboration',
    body: 'Assign tasks, share reference files, and track approvals with role-aware workspaces for production, art, and post.'
  },
  {
    icon: <RocketLaunchRoundedIcon color="primary" sx={{ fontSize: 28 }} />,
    title: 'AI production assists',
    body: 'Surface daily callouts, generate summaries, and draft creative assets with assistants tuned for film and television.'
  },
  {
    icon: <BoltRoundedIcon color="primary" sx={{ fontSize: 28 }} />,
    title: 'Live production pulse',
    body: 'Track dailies, call sheets, and approvals with real-time updates that keep every department aligned.'
  },
  {
    icon: <PaletteRoundedIcon color="primary" sx={{ fontSize: 28 }} />,
    title: 'Visual asset library',
    body: 'Centralize concept art, storyboards, and references with versioning, comments, and secure sharing.'
  },
  {
    icon: <ShieldRoundedIcon color="primary" sx={{ fontSize: 28 }} />,
    title: 'Enterprise-grade security',
    body: 'Granular permissions, SSO options, and audit trails keep your productions safe and compliant.'
  }
];

const imageBasePath = process.env.PUBLIC_URL || '.';

const featureShowcaseSources = [
  {
    label: 'Live dashboard',
    description: 'Milestones, ownership, and approvals at a glance.',
    detail: 'See each production’s phase, tasks, and resource spread with instant clarity on status and blockers.',
    fileName: 'screenshot-84.png',
    alt: 'Dashboard showing production pipeline and resource status'
  },
  {
    label: 'Task management',
    description: 'Kanban-style boards for every department.',
    detail: 'Cards flow from draft to wrap with automated transitions, so approvals never get stuck in inboxes.',
    fileName: 'screenshot-85.png',
    alt: 'Task board view with prioritized items for each team member'
  },
  {
    label: 'Stripboard & scheduling',
    description: 'Breakdown sheets and shooting timelines.',
    detail: 'Stripboards, breakdown sheets, and shooting schedules keep notes connected to every beat.',
    fileName: 'screenshot-81.png',
    alt: 'Script view highlighting scene details and annotations'
  }
];

const featureShowcasePlaceholders = featureShowcaseSources.map((item) => ({
  ...item,
  image: `${imageBasePath}/Images/${encodeURIComponent(item.fileName)}`
}));

function Features() {
  const theme = useTheme();

  useEffect(() => {
    document.title = 'Features • Luminara';
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `radial-gradient(circle at 15% 15%, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 33%),
                     radial-gradient(circle at 90% 5%, ${alpha(theme.palette.secondary.main, 0.15)} 0%, transparent 35%),
                     ${theme.palette.background.default}`,
        py: { xs: 6, md: 10 }
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={{ xs: 6, md: 9 }}>
          <Box
            sx={{
              borderRadius: 4,
              p: { xs: 4, md: 6 },
              background: `linear-gradient(135deg, rgba(0, 198, 255, 0.12), rgba(0, 114, 255, 0.35))`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              boxShadow: '0 30px 60px rgba(4, 31, 63, 0.25)'
            }}
          >
            <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} textAlign={{ xs: 'left', md: 'center' }}>
              <Typography variant="overline" color="primary" fontWeight={600} letterSpacing={1.5}>
                Suite overview
              </Typography>
              <Typography variant="h3" fontWeight={800} sx={{ maxWidth: 680 }}>
                Build productions that feel orchestrated, not chaotic
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
                Luminara layers smart automation, collaboration, and visibility so your crew can focus on creativity while the platform orchestrates the logistics.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    borderRadius: 3,
                    px: 5,
                    py: 1.75,
                    background: 'linear-gradient(120deg, #00c6ff 0%, #0072ff 65%, #0047b3 100%)',
                    boxShadow: '0 20px 50px rgba(0, 114, 255, 0.45)',
                    textTransform: 'none',
                    fontSize: '1rem'
                  }}
                >
                  Start for free
                </Button>
                <Button
                  component={RouterLink}
                  to="/"
                  variant="text"
                  size="large"
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    textTransform: 'none',
                    color: theme.palette.primary.main
                  }}
                >
                  Back to landing
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Stack spacing={4}>
            <Grid container spacing={3}>
              {featuresContent.map((item, index) => (
                <Grid item xs={12} md={6} key={item.title}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                      backgroundColor: alpha(theme.palette.background.paper, 0.96),
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 'auto -30px -30px auto',
                        width: 120,
                        height: 120,
                        borderRadius: 999,
                        background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.35)} 0%, transparent 65%)`,
                        opacity: 0.25
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 3,
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            display: 'grid',
                            placeItems: 'center',
                            color: theme.palette.primary.main
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Typography variant="h5" fontWeight={800}>
                          {item.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          {item.body}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>

          <Stack spacing={4}>
            {featureShowcasePlaceholders.map((item, idx) => (
              <Grid container spacing={3} key={item.label} direction={idx % 2 === 0 ? 'row' : 'row-reverse'}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      overflow: 'hidden',
                      boxShadow: '0 28px 60px rgba(2, 6, 23, 0.35)'
                    }}
                  >
                    <CardMedia component="img" image={item.image} alt={item.alt} sx={{ height: 320, objectFit: 'cover' }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={1.5}>
                      {item.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ my: 1 }}>
                      {item.description}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
                      {item.detail}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            ))}
          </Stack>

          <Box
            sx={{
              borderRadius: 4,
              p: { xs: 4.5, md: 5.5 },
              textAlign: 'center',
              background: `linear-gradient(140deg, rgba(0, 198, 255, 0.15), rgba(26, 115, 232, 0.35))`,
              boxShadow: '0 30px 80px rgba(3, 37, 76, 0.2)'
            }}
          >
            <Stack spacing={2} alignItems="center">
              <Typography variant="h4" fontWeight={800} color="white">
                Ready to choreograph every department?
              </Typography>
              <Typography variant="body1" color="rgba(255,255,255,0.87)" sx={{ maxWidth: 680 }}>
                Launch a studio-grade workspace with a single click and let Luminara handle the orchestration.
              </Typography>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 16, color: 'white' }} />}
                sx={{
                  borderRadius: 3,
                  px: 6,
                  py: 1.75,
                  background: 'linear-gradient(120deg, #4cdfd8 0%, #00a3ff 70%)',
                  boxShadow: '0 20px 60px rgba(0, 163, 255, 0.55)',
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Launch workspace
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

export default Features;
